import { useState } from 'react'
import { Card } from '../../components/Card'
import { Button, ErrorBanner, Field, Select, TextInput } from '../../components/forms'
import { useSession } from '../../auth/session'
import { formatMoneyDocument } from '../../lib/format'
import { numberOrNull } from '../../lib/forms'
import { useOpenSession, usePosContext, usePosLocations, type PosCounter } from '../../features/pos/api'
import { ShopDayPanel } from './ShopDayPanel'
import { Till } from './Till'
import { usePageHeader } from '../../layout/pageHeader'

const LOCATION_KEY = 'dpl.pos.location'

function remembered(): string {
  try {
    return window.localStorage.getItem(LOCATION_KEY) ?? ''
  } catch {
    return ''
  }
}

/**
 * The point of sale: which shop, whether a register session is open, then the till.
 *
 * A branch cashier is at their own shop and is never asked. Someone who can sell
 * anywhere picks a shop once; the choice is remembered on this device, because a
 * till is a device in one place.
 */
export function PointOfSale() {
  const user = useSession((s) => s.user)
  const locations = usePosLocations()
  const [chosen, setChosen] = useState(remembered)

  const fixed = user?.locationId ?? ''
  const locationId = fixed || chosen

  if (!fixed && !chosen) {
    return <ChooseShop locations={locations.data ?? []} error={locations.error} onChoose={choose} />
  }

  function choose(id: string) {
    try {
      window.localStorage.setItem(LOCATION_KEY, id)
    } catch {
      // A browser that refuses storage just asks again next time.
    }
    setChosen(id)
  }

  return <PosAtLocation locationId={locationId} onChangeLocation={fixed ? undefined : () => choose('')} />
}

function ChooseShop({ locations, error, onChoose }: { locations: { id: string; name: string }[]; error: unknown; onChoose: (id: string) => void }) {
  usePageHeader('Point of Sale', 'Choose the shop')
  return (
    <Card title="Choose the shop you are selling at">
      <ErrorBanner error={error} />
      <div className="flex max-w-md items-end gap-3">
        <Field label="Shop" htmlFor="pos-location" className="flex-1">
          <Select id="pos-location" defaultValue="" onChange={(e) => onChoose(e.target.value)}>
            <option value="" disabled>
              Choose…
            </option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <p className="mt-3 text-sm text-dpl-muted">Remembered on this device until you change it.</p>
    </Card>
  )
}

function PosAtLocation({ locationId, onChangeLocation }: { locationId: string; onChangeLocation?: () => void }) {
  const context = usePosContext(locationId)

  if (context.isPending) return <div className="h-64 animate-pulse rounded-lg bg-dpl-surface" aria-busy="true" aria-label="Loading the till" />
  if (context.error || !context.data) return <ErrorBanner error={context.error ?? new Error('The till could not load.')} />

  const ctx = context.data

  if (!ctx.session) {
    return (
      <OpenRegister
        locationId={locationId}
        locationName={ctx.location.name}
        counters={ctx.counters ?? []}
        onChangeLocation={onChangeLocation}
      />
    )
  }

  return <Till context={ctx} onChangeLocation={onChangeLocation} />
}

function OpenRegister({
  locationId, locationName, counters, onChangeLocation,
}: {
  locationId: string
  locationName: string
  counters: PosCounter[]
  onChangeLocation?: () => void
}) {
  usePageHeader('Point of Sale', `${locationName} · the register is closed`)
  const open = useOpenSession(locationId)
  const [float, setFloat] = useState('')
  const amount = numberOrNull(float)

  // Pre-select the only free counter. With one free there is no choice to make, and making a cashier
  // press it anyway is a step at the start of every shift that buys nothing.
  const free = counters.filter((c) => !c.openedBy)
  const [counterId, setCounterId] = useState<string>(() => (free.length === 1 ? free[0].id : ''))

  // A shop that names no counters opens exactly as it always has.
  const needsCounter = counters.length > 0
  const ready = amount !== null && amount >= 0 && (!needsCounter || counterId !== '')

  return (
    <Card title={`Open the register — ${locationName}`}>
      <p className="max-w-xl text-sm text-dpl-text">
        Count the cash in the drawer and enter it as the opening float. Every sale on this till is recorded against your session, and
        at close the drawer is counted against what the session says should be there.
      </p>
      <form
        className="mt-4 flex max-w-md items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (ready) open.mutate({ openingFloat: amount!, counterId: counterId || undefined })
        }}
      >
        {needsCounter && (
          <Field label="Counter" htmlFor="counter" className="flex-1">
            <Select id="counter" value={counterId} onChange={(e) => setCounterId(e.target.value)}>
              <option value="">Choose…</option>
              {counters.map((c) => (
                // Taken ones stay on the list, named and unselectable. Hiding them makes a cashier
                // think their counter has been deleted; saying who has it sends them to that person.
                <option key={c.id} value={c.id} disabled={Boolean(c.openedBy)}>
                  {c.openedBy ? `${c.name} — ${c.openedBy} has it` : c.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Opening float (KSh)" htmlFor="opening-float" className="flex-1" hint={amount !== null ? formatMoneyDocument(amount) : undefined}>
          <TextInput id="opening-float" inputMode="decimal" autoFocus value={float} onChange={(e) => setFloat(e.target.value)} />
        </Field>
        <Button type="submit" busy={open.isPending} disabled={!ready}>
          Open register
        </Button>
      </form>
      <div className="mt-3">
        <ErrorBanner error={open.error} />
      </div>

      {/* The shop's own day sits under the drawer, because they are answered at the same moment by
          the same person: count the drawer, then say what the till and the bank hold. */}
      <div className="mt-6">
        <ShopDayPanel locationId={locationId} />
      </div>
      {onChangeLocation && (
        <Button tone="ghost" className="mt-4" onClick={onChangeLocation}>
          Sell at a different shop
        </Button>
      )}
    </Card>
  )
}
